import * as React from 'react';

import { Campo } from '@/componentes/campo';
import { Input } from '@/componentes/ui/input';
import { cn } from '@/lib/utils';

/* Percentual em PONTOS (12,5 é doze e meio por cento), o formato que o usuário
 * digita. Tudo que sai do motor é FRAÇÃO e não passa por aqui.
 *
 * Estava escrito duas vezes, em Empresa e em Despesas, e as duas cópias já
 * tinham divergido: uma reservava 36px para o "%" e a outra 32px, então o
 * mesmo controle desenhava o sinal a distâncias diferentes em duas telas do
 * mesmo funil. É uma decisão só (campo curto, número à direita, "%" fixo na
 * borda) e agora mora num lugar só.
 *
 * `type="number"` porque percentual é grandeza simples, sem separador de
 * milhar: quem tem máscara é a moeda, em `CampoMoeda`. O "%" é decorativo e
 * fica fora da árvore acessível; quem diz a unidade é o rótulo. */

export type EntradaPercentualProps = Omit<
  React.ComponentProps<'input'>,
  'value' | 'onChange' | 'type'
> & {
  valor: string;
  aoMudar: (valor: string) => void;
};

export function EntradaPercentual({
  valor,
  aoMudar,
  min = 0,
  max,
  step = 0.1,
  placeholder = '0',
  className,
  ...resto
}: EntradaPercentualProps) {
  return (
    // O controle PREENCHE a célula: quem dimensiona é a grade da tela
    // (@campos e afins). Um teto no próprio controle deixava um buraco à
    // direita de cada campo, em toda célula maior que o teto. O invólucro
    // ancora o "%", então ele acompanha a borda do campo.
    <div className="relative">
      <Input
        {...resto}
        type="number"
        min={min}
        max={max}
        step={step}
        inputMode="decimal"
        placeholder={placeholder}
        value={valor}
        onChange={(evento) => aoMudar(evento.currentTarget.value)}
        className={cn(
          'numero pr-8 text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
          className,
        )}
      />
      <span
        className="text-tinta-3 pointer-events-none absolute inset-y-0 right-3 flex items-center text-nota"
        aria-hidden="true"
      >
        %
      </span>
    </div>
  );
}

export type CampoPercentualProps = {
  rotulo: string;
  ajuda?: string | undefined;
  /** O conceito por trás do número, numa tooltip no rótulo. Ver `Campo`. */
  dica?: React.ReactNode;
  erro?: string | undefined;
  valor: string;
  aoMudar: (valor: string) => void;
  className?: string | undefined;
  disabled?: boolean | undefined;
  max?: number | undefined;
  placeholder?: string | undefined;
};

export function CampoPercentual({
  rotulo,
  ajuda,
  dica,
  erro,
  valor,
  aoMudar,
  className,
  disabled,
  max,
  placeholder,
}: CampoPercentualProps) {
  return (
    <Campo rotulo={rotulo} ajuda={ajuda} dica={dica} erro={erro} className={className}>
      {(atributos) => (
        <EntradaPercentual
          {...atributos}
          valor={valor}
          aoMudar={aoMudar}
          disabled={disabled}
          max={max}
          placeholder={placeholder}
        />
      )}
    </Campo>
  );
}
